import {
  Activity,
  ArrowUpRight,
  BarChart3,
  Calendar,
  Clock,
  ExternalLink,
  Eye,
  FileCheck2,
  FileEdit,
  FileText,
  Layers,
  LucideIcon,
  PenSquare,
  Sparkles,
  TrendingUp,
  UserCheck
} from "lucide-react";
import { Link } from "react-router-dom";
import { ErrorState, LoadingState } from "../../components/LoadingState";
import { TrafficOverviewCard } from "../../components/TrafficOverviewCard";
import { useApi } from "../../hooks/useApi";
import { Dashboard } from "../../types";

export function DashboardPage() {
  const { data, loading, error } = useApi<Dashboard>("/api/admin/dashboard", []);
  if (loading) return <LoadingState label="Loading newsroom signals..." />;
  if (error) return <ErrorState message={error} />;
  if (!data) return null;

  return (
    <div className="admin-page-flow">
      {/* Editorial Page Masthead Header */}
      <header className="admin-section-header">
        <div>
          <div className="admin-breadcrumb-tag">
            <Sparkles size={13} className="text-[#C99A2E]" />
            <span>EXECUTIVE DISPATCH & SIGNALS</span>
          </div>
          <h1 className="admin-title">Newsroom Operations</h1>
          <p className="admin-subtitle">
            Real-time publishing telemetry, editorial queue status, and audience signals for ZimSmart Magazine.
          </p>
        </div>

        <div className="admin-header-actions">
          <Link to="/admin/homepage" className="admin-btn-secondary">
            <Layers size={15} />
            <span>Curate Front Page</span>
          </Link>
          <Link to="/admin/articles/new" className="admin-btn-primary">
            <PenSquare size={15} />
            <span>Write New Story</span>
          </Link>
        </div>
      </header>

      {/* Standardized Metric Cards Grid */}
      <div className="admin-metrics-grid">
        <MetricCard
          icon={FileEdit}
          label="Drafts in Progress"
          value={data.publishing.drafts ?? 0}
          note="Unpublished manuscripts"
          tone="neutral"
        />
        <MetricCard
          icon={Clock}
          label="Awaiting Review"
          value={data.publishing.awaiting_review ?? 0}
          note="Pending editor approval"
          tone="amber"
        />
        <MetricCard
          icon={Calendar}
          label="Scheduled Queue"
          value={data.publishing.scheduled ?? 0}
          note="Ready for automated release"
          tone="emerald"
        />
        <MetricCard
          icon={Eye}
          label="Tracked Article Views"
          value={data.performance.views ?? 0}
          note="Live reader impressions"
          tone="gold"
        />
      </div>

      {/* Visual Audience Traffic Overview Card powered by Recharts */}
      <div className="admin-chart-section">
        <TrafficOverviewCard
          initialHistory={data.performance.dailyViews}
          todayViews={data.performance.todayViews ?? 0}
          yesterdayViews={data.performance.yesterdayViews ?? 0}
          views7Days={data.performance.views7Days ?? 0}
          growth7d={data.performance.growth7d ?? 0}
          dailyAverage={data.performance.dailyAverage ?? 0}
          peakDay={data.performance.peakDay}
        />
      </div>

      {!data.performance.analyticsConfigured && (
        <div className="admin-info-banner">
          <div className="flex items-center gap-2">
            <Activity size={16} className="text-[#C99A2E]" />
            <span className="font-semibold text-slate-800">Native Audience Telemetry Active</span>
          </div>
          <p className="text-xs text-slate-600 mt-1">
            Tracking reader engagement directly via ZimSmart edge analytics and impression telemetry.
          </p>
        </div>
      )}

      {/* 2-Column Editorial Insights: Most Read & Activity Log */}
      <div className="admin-grid-two-col">
        {/* Most Read Articles */}
        <section className="admin-card-panel">
          <div className="admin-panel-heading">
            <div className="flex items-center gap-2">
              <TrendingUp size={16} className="text-[#C99A2E]" />
              <h2>Top Editorial Stories</h2>
            </div>
            <Link to="/admin/articles" className="admin-panel-link">
              <span>View All Articles</span>
              <ArrowUpRight size={13} />
            </Link>
          </div>

          {data.performance.mostRead.length ? (
            <div className="admin-table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th style={{ width: "45px" }}>#</th>
                    <th>Headline</th>
                    <th style={{ width: "90px", textAlign: "right" }}>Impressions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.performance.mostRead.map((article, idx) => (
                    <tr key={article.id}>
                      <td>
                        <span className={`admin-rank-badge ${idx === 0 ? "gold" : ""}`}>
                          {idx + 1}
                        </span>
                      </td>
                      <td>
                        <div className="admin-story-cell">
                          <Link
                            to={`/admin/articles/${article.id}/edit`}
                            className="admin-story-title"
                          >
                            {article.title}
                          </Link>
                        </div>
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <div className="admin-views-pill">
                          <Eye size={12} className="text-slate-400" />
                          <span>{article.views.toLocaleString()}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="admin-empty-panel">
              <p>No story views tracked yet in this reporting window.</p>
            </div>
          )}
        </section>

        {/* Editorial Audit & Activity Feed */}
        <section className="admin-card-panel">
          <div className="admin-panel-heading">
            <div className="flex items-center gap-2">
              <Activity size={16} className="text-[#0F766E]" />
              <h2>Newsroom Activity Feed</h2>
            </div>
            <span className="admin-panel-meta">AUDIT TRAIL</span>
          </div>

          {data.editorial.recentActivity.length ? (
            <div className="admin-activity-timeline">
              {data.editorial.recentActivity.map((item) => (
                <div key={item.id} className="admin-activity-item">
                  <div className="admin-activity-dot" />
                  <div className="admin-activity-body">
                    <p className="admin-activity-text">{item.action}</p>
                    <div className="admin-activity-meta">
                      <span className="admin-activity-user">
                        {item.user_name || "System Automated"}
                      </span>
                      <span className="meta-sep">•</span>
                      <span className="admin-activity-time">Logged by Newsroom Engine</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="admin-empty-panel">
              <p>No recent newsroom audit records.</p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

interface MetricCardProps {
  icon: LucideIcon;
  label: string;
  value: number;
  note: string;
  tone: "neutral" | "amber" | "emerald" | "gold";
}

function MetricCard({ icon: Icon, label, value, note, tone }: MetricCardProps) {
  const toneClasses = {
    neutral: "tone-neutral",
    amber: "tone-amber",
    emerald: "tone-emerald",
    gold: "tone-gold"
  };

  return (
    <div className={`admin-metric-box ${toneClasses[tone]}`}>
      <div className="admin-metric-top">
        <span className="admin-metric-label">{label}</span>
        <div className="admin-metric-icon-wrap">
          <Icon size={18} />
        </div>
      </div>
      <div className="admin-metric-value">{value.toLocaleString()}</div>
      <div className="admin-metric-note">{note}</div>
    </div>
  );
}

